// 确保类型声明被加载
import './App.vue';

import { createApp, watch } from "vue";
import App from "./App.vue";
import { createPinia } from "pinia";
import i18n from './assets/i18n/i18n';
import router from './router';
import { useUserStore } from './store';
import NProgress from 'nprogress';
import 'nprogress/nprogress.css';

NProgress.configure({ showSpinner: false });

// ============================================
// 🎨 新的统一设计系统 (Design System v2.0)
// ============================================
// 1. Design Tokens - 单一事实来源
import './assets/styles/tokens.css';
// 2. Element Plus 适配层
import './assets/styles/element-adapter.css';
// 3. Global Utilities
import './assets/styles/utilities.css';
// 3. Element Plus 样式覆盖
import './assets/styles/element-overrides.css';

import { enableLog } from '@/utils/app-runtime';
import { setupGlobalDOMErrorHandling } from './utils/dom';
import { syncRumUser } from '@/utils/rum';

// 导入Element Plus
import ElementPlus from 'element-plus';
import zhCn from 'element-plus/es/locale/lang/zh-cn';
import 'element-plus/dist/index.css';
import * as ElementPlusIconsVue from '@element-plus/icons-vue';

const setupApp = async () => {
  NProgress.start();
  const app = createApp(App);
  const pinia = createPinia();

  // 注册Element Plus
  app.use(ElementPlus, { locale: zhCn });

  // 注册Element Plus图标
  for (const [key, component] of Object.entries(ElementPlusIconsVue)) {
    app.component(key, component);
  }

  // 设置全局DOM错误处理
  setupGlobalDOMErrorHandling();

  // 全局错误处理
  app.config.errorHandler = (err: any, _instance: any, info: any) => {
    // 检查是否是DOM相关错误，静默处理
    if (err instanceof Error && err.message.includes('parentNode')) {
      return;
    }

    // 其他错误正常处理
    if (enableLog) {
      console.error('[Main] Vue应用错误', {
        error: err,
        context: { info, component: _instance?.$options?.name }
      });
    }
  };

  // Vue 警告升级为异常 - Fail-Fast 原则
  app.config.warnHandler = (msg, _instance, trace) => {
    // 对关键模块的警告直接升级为 Error
    if (String(msg).includes('provide/inject') ||
      String(msg).includes('Missing required prop') ||
      String(msg).includes('Invalid prop')) {
      throw new Error(`[VueWarnAsError] ${msg} ${trace ?? ''}`)
    }

    // 其他警告正常输出
    if (enableLog) {
      console.warn('[Main] Vue警告', { msg, trace })
    }
  };

  // 1. 注册全局插件
  app.use(pinia);
  app.use(i18n);

  // 2. 初始化用户状态
  const userStore = useUserStore();
  try {
    await userStore.initialize();
  } catch (error) {
    if (error && typeof error === 'object' && 'response' in error &&
      (error as any).response?.status === 401) {
      // 用户未登录，静默处理
    } else {
      if (enableLog) {
        console.error('[Main] 用户状态初始化失败', {
          error,
          context: { operation: 'userStoreInitialize' }
        });
      }
    }
  }
  watch(
    () => userStore.currentUser,
    (user) => syncRumUser(user),
    { immediate: true, deep: true }
  );

  // 3. 注册路由
  app.use(router);

  // 4. 挂载应用
  app.mount("#app");
  NProgress.done();
};

// 全局未捕获Promise错误处理
window.addEventListener('unhandledrejection', (event) => {
  // 检查是否是DOM相关错误，静默处理
  if (event.reason instanceof Error && event.reason.message.includes('parentNode')) {
    event.preventDefault();
    return;
  }
});

// 启动应用
setupApp().catch(error => {
  if (enableLog) {
    console.error('[Main] [FATAL] 应用启动失败', {
      error,
      context: { operation: 'setupApp' }
    });
  }
});
